import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Card, CardContent, Checkbox, Chip, FormControlLabel, FormGroup, Grid, InputAdornment,
  MenuItem, Radio, RadioGroup, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography,
} from '@mui/material';
import { PlayArrow, Save } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { formatMoney } from '../../utils/currency';
import { getApiErrorMessage } from '../../services/userService';
import { AutoPayoutSchedule, PayoutSettings, payoutApi } from '../../services/business/payoutService';
import { PARTNER_TYPES } from './payoutShared';

const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DEFAULT_AUTO_PAYOUT: AutoPayoutSchedule = {
  enabled: false, frequency: 'weekly', weekDays: [1], monthDay: 1, time: '10:00', minWalletBalance: 0,
};

function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
}

function describeSchedule(a: AutoPayoutSchedule): string {
  if (a.frequency === 'daily') return `every day at ${a.time}`;
  if (a.frequency === 'weekly') {
    const days = [...a.weekDays].sort().map((d) => WEEK_DAYS[d]);
    return days.length ? `every ${days.join(', ')} at ${a.time}` : 'no day selected';
  }
  return `on the ${ordinal(a.monthDay)} of every month at ${a.time}${a.monthDay > 28 ? ' (last day in shorter months)' : ''}`;
}

function Section({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ mb: 2.5 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={800}>{title}</Typography>
        {subtitle && <Typography variant="body2" color="text.secondary" mb={2}>{subtitle}</Typography>}
        {children}
      </CardContent>
    </Card>
  );
}

export default function PayoutSettingsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ['payout-settings'], queryFn: payoutApi.settings });
  const { data: stripe } = useQuery({ queryKey: ['payout-stripe-status'], queryFn: payoutApi.stripeStatus });
  const [form, setForm] = useState<PayoutSettings | null>(null);
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (data) setForm({ ...data, autoPayout: { ...DEFAULT_AUTO_PAYOUT, ...(data.autoPayout || {}) } });
  }, [data]);

  const save = useMutation({
    mutationFn: () => {
      const { autoPayoutStatus: _status, ...rest } = form!;
      const a = form!.autoPayout;
      return payoutApi.saveSettings({
        ...rest,
        allowedMethods: ['bank'],
        allowedUserTypes: rest.allowedUserTypes.filter((t) => PARTNER_TYPES.some((p) => p.value === t)),
        autoPayout: {
          enabled: a.enabled, frequency: a.frequency, weekDays: a.weekDays, monthDay: a.monthDay, time: a.time, minWalletBalance: a.minWalletBalance,
        },
        stripe: {
          managedBankPayouts: !!form!.stripe?.managedBankPayouts,
          financialAccountId: form!.stripe?.financialAccountId || '',
        },
      });
    },
    onSuccess: (saved) => {
      setForm({ ...saved, autoPayout: { ...DEFAULT_AUTO_PAYOUT, ...(saved.autoPayout || {}) } });
      setFlash({ type: 'success', text: 'Payout settings saved' });
      queryClient.invalidateQueries({ queryKey: ['payout-settings'] });
      queryClient.invalidateQueries({ queryKey: ['payout-stripe-status'] });
    },
    onError: (e) => setFlash({ type: 'error', text: getApiErrorMessage(e, 'Could not save settings') }),
  });

  const runNow = useMutation({
    mutationFn: payoutApi.runAutoPayoutNow,
    onSuccess: (r) => {
      setFlash({
        type: 'success',
        text: `${r.paid} of ${r.created} payouts paid (${formatMoney(r.totalAmount, stripe?.currency || 'USD')}), `
          + `${r.skipped} skipped${r.errors ? `, ${r.errors} errors` : ''}.`,
      });
      queryClient.invalidateQueries({ queryKey: ['payout-settings'] });
    },
    onError: (e) => setFlash({ type: 'error', text: getApiErrorMessage(e, 'Could not run payouts') }),
  });

  if (isLoading || !form) {
    return <PageShell>{error ? <Alert severity="error">{getApiErrorMessage(error)}</Alert> : <Typography>Loading…</Typography>}</PageShell>;
  }

  const set = <K extends keyof PayoutSettings>(key: K, value: PayoutSettings[K]) => setForm((f) => (f ? { ...f, [key]: value } : f));
  const setAuto = (patch: Partial<AutoPayoutSchedule>) => setForm((f) => (f ? { ...f, autoPayout: { ...f.autoPayout, ...patch } } : f));
  const num = (key: keyof PayoutSettings, label: string, help?: string, adornment?: string) => (
    <TextField
      fullWidth size="small" type="number" label={label} helperText={help}
      value={form[key] as number}
      onChange={(e) => set(key, Number(e.target.value) as never)}
      inputProps={{ min: 0, step: 'any' }}
      InputProps={adornment ? { endAdornment: <InputAdornment position="end">{adornment}</InputAdornment> } : undefined}
    />
  );
  const toggle = (key: keyof PayoutSettings, label: string, help?: string) => (
    <Box>
      <FormControlLabel control={<Switch checked={!!form[key]} onChange={(e) => set(key, e.target.checked as never)} />} label={label} />
      {help && <Typography variant="caption" color="text.secondary" display="block" ml={6} mt={-0.5}>{help}</Typography>}
    </Box>
  );
  const stripeReady = !!stripe?.configured;
  const auto = form.autoPayout;
  const status = form.autoPayoutStatus;
  const currency = stripe?.currency || 'USD';
  const stripeBankPayouts = form.gateway === 'stripe' && !!form.stripe?.managedBankPayouts;

  return (
    <PageShell>
      <PageHeader
        eyebrow="Finance"
        title="Payout Settings"
        subtitle="Send partner and provider wallet balances to their verified bank accounts, on a schedule or when they request it."
        actions={<Button variant="contained" startIcon={<Save />} onClick={() => save.mutate()} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save settings'}</Button>}
      />
      {flash && <Alert severity={flash.type} onClose={() => setFlash(null)} sx={{ mb: 2 }}>{flash.text}</Alert>}

      <Section
        title="Automatic payouts (schedule)"
        subtitle="On each payout day, everything above the minimum wallet balance is sent to the partner's verified bank account and deducted from their wallet."
      >
        <FormControlLabel
          control={<Switch checked={auto.enabled} onChange={(e) => setAuto({ enabled: e.target.checked })} />}
          label={<Typography fontWeight={700}>Pay partners automatically</Typography>}
        />
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" mb={1}>How often</Typography>
            <ToggleButtonGroup
              exclusive fullWidth size="small" color="primary" value={auto.frequency} disabled={!auto.enabled}
              onChange={(_, v) => v && setAuto({ frequency: v })}
            >
              <ToggleButton value="daily">Daily</ToggleButton>
              <ToggleButton value="weekly">Weekly</ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs={12} md={5}>
            {auto.frequency === 'weekly' && (
              <>
                <Typography variant="subtitle2" mb={1}>Payout days</Typography>
                <ToggleButtonGroup
                  size="small" color="primary" value={auto.weekDays} disabled={!auto.enabled}
                  onChange={(_, v: number[]) => setAuto({ weekDays: [...v].sort() })}
                >
                  {WEEK_DAYS.map((d, i) => <ToggleButton key={d} value={i} sx={{ px: 1.5 }}>{d}</ToggleButton>)}
                </ToggleButtonGroup>
              </>
            )}
            {auto.frequency === 'monthly' && (
              <TextField
                select fullWidth size="small" label="Day of month" value={auto.monthDay} disabled={!auto.enabled} sx={{ mt: 3.5 }}
                onChange={(e) => setAuto({ monthDay: Number(e.target.value) })}
                helperText="29–31 run on the last day in shorter months"
              >
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <MenuItem key={d} value={d}>{ordinal(d)}</MenuItem>)}
              </TextField>
            )}
            {auto.frequency === 'daily' && (
              <Typography variant="body2" color="text.secondary" mt={4}>Runs every day.</Typography>
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth size="small" type="time" label="Time" value={auto.time} disabled={!auto.enabled} sx={{ mt: 3.5 }}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setAuto({ time: e.target.value })}
              helperText={status?.timezone ? `Timezone: ${status.timezone}` : undefined}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth size="small" type="number" label="Minimum balance kept in wallet" disabled={!auto.enabled}
              value={auto.minWalletBalance}
              onChange={(e) => setAuto({ minWalletBalance: Number(e.target.value) })}
              inputProps={{ min: 0, step: 'any' }}
              InputProps={{ endAdornment: <InputAdornment position="end">{currency}</InputAdornment> }}
              helperText="Only the amount above this is paid out"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            {auto.enabled && (
              <Alert severity="info" icon={false}>
                Pays out {describeSchedule(auto)}. Example: wallet {formatMoney(auto.minWalletBalance + 500, currency)} → sends{' '}
                <b>{formatMoney(500, currency)}</b>, keeps {formatMoney(auto.minWalletBalance, currency)}.
                {' '}Skipped when the amount is below the minimum per request ({formatMoney(form.minAmount, currency)}), KYC is not approved,
                {' '}or there is no verified bank account.
              </Alert>
            )}
          </Grid>
        </Grid>
        <Alert severity={stripeBankPayouts ? 'success' : 'warning'} sx={{ mt: 2 }}>
          {stripeBankPayouts
            ? 'Each payout is sent through Stripe to the partner\'s US or Indian bank account and marked Paid immediately. If a bank rejects it later, the money returns to the wallet automatically. Banks in other countries are marked Paid; send those transfers from your bank.'
            : 'Gateway is manual: payouts are marked Paid immediately, but no money moves automatically. Send each transfer from your bank (see Withdrawals → Paid). To send money automatically, select the Stripe gateway and turn on "Pay partner bank accounts through Stripe" below.'}
        </Alert>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" mt={2}>
          {auto.enabled && status?.nextRunAt && (
            <Chip color="primary" variant="outlined" label={`Next run: ${new Date(status.nextRunAt).toLocaleString()}`} />
          )}
          {status?.lastRun && (
            <Chip
              variant="outlined"
              label={`Last run ${new Date(status.lastRun.at).toLocaleString()} (${status.lastRun.trigger === 'admin' ? 'manual' : 'scheduled'}): `
                + `${status.lastRun.created} paid out · ${formatMoney(status.lastRun.totalAmount, currency)} · ${status.lastRun.skipped} skipped`}
            />
          )}
          <Box flex={1} />
          <Button
            variant="outlined" startIcon={<PlayArrow />} disabled={runNow.isPending}
            onClick={() => {
              if (window.confirm(`Pay every eligible partner now? Everything above ${formatMoney(auto.minWalletBalance, currency)} (saved setting) leaves their wallet.`)) runNow.mutate();
            }}
          >
            {runNow.isPending ? 'Running…' : 'Run payouts now'}
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.5} textAlign="right">
          Run now uses the saved settings. Save first if you changed anything.
        </Typography>
        {!!status?.lastRun?.skippedPartners?.length && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            <b>Not paid in the last run</b>
            <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
              {status.lastRun.skippedPartners.map((p) => (
                <li key={p.userId}><b>{p.name}</b>: {p.reason}</li>
              ))}
            </Box>
          </Alert>
        )}
      </Section>

      <Section title="Partner withdrawal requests" subtitle="Partners can also request a withdrawal from the app; you approve it and the money goes to their bank account.">
        {toggle('withdrawalsEnabled', 'Allow partners to request withdrawals')}
        <Grid container spacing={2} mt={0.5}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" mb={1}>Approval mode</Typography>
            <RadioGroup value={form.mode} onChange={(e) => set('mode', e.target.value as PayoutSettings['mode'])}>
              <FormControlLabel value="manual" control={<Radio />} label={(
                <Box><Typography fontWeight={700}>Admin approves</Typography>
                  <Typography variant="body2" color="text.secondary">
                    The request shows as Processing to the partner. When an admin clicks &quot;Approve &amp; pay&quot; in Withdrawals, it is paid.
                  </Typography></Box>
              )} />
              <FormControlLabel value="automatic" control={<Radio />} label={(
                <Box><Typography fontWeight={700}>Pay instantly</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requests are paid immediately, without admin approval. Amounts above the auto-pay limit still wait for an admin.
                  </Typography></Box>
              )} />
            </RadioGroup>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" mb={1}>Payout gateway</Typography>
            <TextField select fullWidth size="small" value={form.gateway}
              onChange={(e) => set('gateway', e.target.value as PayoutSettings['gateway'])}>
              <MenuItem value="manual">Manual bank transfer (admin pays outside the platform)</MenuItem>
              <MenuItem value="stripe" disabled={!stripeReady}>Stripe (sends money to partner bank accounts)</MenuItem>
            </TextField>
            {!stripeReady && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {stripe?.message || 'Stripe is not configured.'} <RouterLink to="/config/payments">Configure Stripe</RouterLink> to enable Stripe payouts.
              </Alert>
            )}
            {form.mode === 'automatic' && (
              <Box mt={2}>{num('autoApproveMaxAmount', 'Auto-pay up to (per request)', '0 = no cap. Requests above this wait for admin approval.')}</Box>
            )}
          </Grid>
        </Grid>
      </Section>

      <Section
        title="Pay partner bank accounts through Stripe"
        subtitle="Partners never sign up for Stripe. They add a US or Indian bank account in the app; after you verify it, Stripe Global Payouts sends payouts straight to that bank."
      >
        {stripe?.configured && (
          <Stack direction="row" gap={1} flexWrap="wrap" mb={1}>
            <Chip label={stripe.sandbox ? 'Test mode' : 'Live mode'} color={stripe.sandbox ? 'warning' : 'success'} variant="outlined" />
            {stripe.stub && <Chip color="warning" label="Stub mode (no real transfers)" />}
          </Stack>
        )}
        <FormControlLabel
          control={(
            <Switch
              checked={!!form.stripe?.managedBankPayouts}
              disabled={form.gateway !== 'stripe'}
              onChange={(e) => set('stripe', { ...form.stripe, managedBankPayouts: e.target.checked })}
            />
          )}
          label="Send payouts to US and Indian bank accounts via Stripe"
        />
        {form.gateway !== 'stripe' && (
          <Typography variant="caption" color="text.secondary" display="block" ml={6} mt={-0.5}>Select the Stripe payout gateway first.</Typography>
        )}
        {form.stripe?.managedBankPayouts && (
          <>
            {stripe?.globalPayouts && (
              stripe.globalPayouts.error ? (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Stripe Global Payouts is not ready: {stripe.globalPayouts.error}
                </Alert>
              ) : (
                <Stack direction="row" gap={1} flexWrap="wrap" mt={1}>
                  <Chip color="success" variant="outlined" label={`Paying from ${stripe.globalPayouts.financialAccountId}`} />
                  {Object.entries(stripe.globalPayouts.available).map(([cur, amt]) => (
                    <Chip key={cur} variant="outlined" label={`Payouts balance ${formatMoney(amt, cur.toUpperCase())}`} />
                  ))}
                </Stack>
              )
            )}
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} md={5}>
                <TextField
                  fullWidth size="small" label="Stripe financial account (optional)"
                  value={form.stripe?.financialAccountId || ''}
                  placeholder="fa_… (auto-detected when empty)"
                  onChange={(e) => set('stripe', { ...form.stripe, financialAccountId: e.target.value.trim() })}
                  helperText="Only needed if your Stripe account has more than one payouts balance"
                />
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2 }}>
              <b>How it works</b>
              <ol style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                <li>The partner submits KYC and adds a bank account in the app: <b>US</b> (routing + account number) or <b>India</b> (IFSC + account number).</li>
                <li>You approve the KYC, then verify the bank account. The partner is registered with Stripe as a payout recipient at this moment. If anything is missing, you see exactly what.</li>
                <li>On each scheduled payout day, or when you approve a request, Stripe sends the money from your payouts balance to the partner&apos;s bank. US: ACH, usually 0–1 business days. India: INR to the local bank, usually 1–7 days, converted by Stripe.</li>
                <li>If the bank rejects or returns a payout, the amount goes back to the partner&apos;s wallet automatically.</li>
              </ol>
              Setup in Stripe: enable <b>Global Payouts</b> (Dashboard → Payouts), add funds to the payouts balance, and request <b>cross-border payouts</b> access for India.
              In live mode, use a restricted key with Recipient Configuration, Financial Accounts (read), Payout Methods and Outbound Payments permissions.
              Bank accounts in other countries stay on manual payout.
            </Alert>
          </>
        )}
      </Section>

      <Section title="Limits" subtitle={`Amounts in ${currency}. Use 0 for "no limit" where noted. Daily and open-request limits apply to partner requests, not scheduled payouts.`}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>{num('minAmount', 'Minimum per payout')}</Grid>
          <Grid item xs={12} sm={6} md={2.4}>{num('maxAmount', 'Maximum per request', '0 = no limit')}</Grid>
          <Grid item xs={12} sm={6} md={2.4}>{num('dailyMaxAmount', 'Daily amount limit', '0 = no limit')}</Grid>
          <Grid item xs={12} sm={6} md={2.4}>{num('dailyMaxCount', 'Requests per day', '0 = no limit')}</Grid>
          <Grid item xs={12} sm={6} md={2.4}>{num('maxOpenRequests', 'Open requests at once')}</Grid>
        </Grid>
      </Section>

      <Section title="Withdrawal fee" subtitle="Deducted from the amount; the partner receives the rest. Applies to scheduled payouts too.">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField select fullWidth size="small" label="Fee type" value={form.feeType} onChange={(e) => set('feeType', e.target.value as PayoutSettings['feeType'])}>
              <MenuItem value="none">No fee</MenuItem>
              <MenuItem value="flat">Flat amount</MenuItem>
              <MenuItem value="percent">Percent</MenuItem>
            </TextField>
          </Grid>
          {form.feeType !== 'none' && (
            <Grid item xs={12} sm={6} md={3}>{num('feeValue', form.feeType === 'percent' ? 'Fee %' : 'Fee amount', undefined, form.feeType === 'percent' ? '%' : undefined)}</Grid>
          )}
        </Grid>
      </Section>

      <Section title="Security & verification" subtitle="Checks a partner must pass before money can leave their wallet.">
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            {toggle('requireKyc', 'Require approved KYC', 'Partner identity (ID document) must be approved by an admin.')}
            {toggle('requireVerifiedAccount', 'Require verified bank account', 'Bank accounts need admin verification before payouts.')}
            {toggle('blockDuplicateAccounts', 'Block shared bank accounts', 'Refuse a bank account already registered by another partner.')}
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack gap={2}>
              {num('newAccountCoolingHours', 'New account waiting period', 'Blocks quick account-swap fraud after a takeover.', 'hours')}
              {num('topupHoldDays', 'Hold card top-ups for', 'Top-ups younger than this cannot be withdrawn (stolen-card guard).', 'days')}
              {num('maxAccountsPerPartner', 'Bank accounts per partner')}
            </Stack>
          </Grid>
        </Grid>
      </Section>

      <Section title="Partners">
        <Typography variant="subtitle2">Partner types that get paid out</Typography>
        <FormGroup row>
          {PARTNER_TYPES.map((t) => (
            <FormControlLabel key={t.value} label={t.label} control={(
              <Checkbox checked={form.allowedUserTypes.includes(t.value)} onChange={(e) => set('allowedUserTypes', e.target.checked
                ? [...form.allowedUserTypes, t.value] : form.allowedUserTypes.filter((x) => x !== t.value))} />
            )} />
          ))}
        </FormGroup>
      </Section>
    </PageShell>
  );
}
